'use client';
import * as React from 'react';
import type { RefObject } from '@mui/x-internals/types';
import type { EventListenerOptions } from '@mui/x-internals/EventManager';
import type { GridEventListener, GridEvents } from '../../models/events';
import type { UnregisterToken } from '../../utils/cleanupTracking/CleanupTracking';
import { TimerBasedCleanupTracking } from '../../utils/cleanupTracking/TimerBasedCleanupTracking';
import { FinalizationRegistryBasedCleanupTracking } from '../../utils/cleanupTracking/FinalizationRegistryBasedCleanupTracking';
import type { GridApiCommon } from '../../models';

// Based on https://github.com/Bnaya/use-dispose-uncommitted/blob/main/src/finalization-registry-based-impl.ts
// Check https://github.com/react/react/issues/15317 to get more information

// We use class to make it easier to detect in heap snapshots by name
class ObjectToBeRetainedByReact {
  static create() {
    return new ObjectToBeRetainedByReact();
  }
}

const registryContainer = {
  current: createRegistry(),
};

let cleanupTokensCounter = 0;

export function useGridEvent<Api extends GridApiCommon, E extends GridEvents>(
  apiRef: RefObject<Api>,
  eventName: E,
  handler?: GridEventListener<E, Api>,
  options?: EventListenerOptions,
) {
  const objectRetainedByReact = React.useState(ObjectToBeRetainedByReact.create)[0];
  const subscription = React.useRef<(() => void) | null>(null);
  const handlerRef = React.useRef<GridEventListener<E, Api> | undefined>(null);
  handlerRef.current = handler;
  const cleanupTokenRef = React.useRef<UnregisterToken | null>(null);

  if (!subscription.current && handlerRef.current) {
    // The event system is typed against `GridApiCommon`, while the handler can be typed
    // against the package-specific API: `any` bridges the two at this single boundary.
    const enhancedHandler: GridEventListener<E, any> = (params, event, details) => {
      // Check for the existence of the event once more to avoid Safari 26 issue
      // https://github.com/mui/mui-x/issues/20159
      if (event && !event.defaultMuiPrevented) {
        handlerRef.current?.(params, event, details);
      }
    };

    subscription.current = apiRef.current.subscribeEvent(eventName, enhancedHandler, options);

    cleanupTokensCounter += 1;
    cleanupTokenRef.current = { cleanupToken: cleanupTokensCounter };

    registryContainer.current.register(
      objectRetainedByReact, // The callback below will be called once this reference stops being retained
      () => {
        subscription.current?.();
        subscription.current = null;
        cleanupTokenRef.current = null;
      },
      cleanupTokenRef.current,
    );
  } else if (!handlerRef.current && subscription.current) {
    subscription.current();
    subscription.current = null;

    if (cleanupTokenRef.current) {
      registryContainer.current.unregister(cleanupTokenRef.current);
      cleanupTokenRef.current = null;
    }
  }

  React.useEffect(() => {
    if (!subscription.current && handlerRef.current) {
      const enhancedHandler: GridEventListener<E, any> = (params, event, details) => {
        // Check for the existence of the event once more to avoid Safari 26 issue
        // https://github.com/mui/mui-x/issues/20159
        if (event && !event.defaultMuiPrevented) {
          handlerRef.current?.(params, event, details);
        }
      };

      subscription.current = apiRef.current.subscribeEvent(eventName, enhancedHandler, options);
    }

    if (cleanupTokenRef.current && registryContainer.current) {
      // If the effect was called, it means that this render was committed
      // so we can trust the cleanup function to remove the listener.
      registryContainer.current.unregister(cleanupTokenRef.current);
      cleanupTokenRef.current = null;
    }

    return () => {
      subscription.current?.();
      subscription.current = null;
    };
  }, [apiRef, eventName, options]);
}

const OPTIONS_IS_FIRST: EventListenerOptions = { isFirst: true };

export function useGridEventPriority<Api extends GridApiCommon, E extends GridEvents>(
  apiRef: RefObject<Api>,
  eventName: E,
  handler?: GridEventListener<E, Api>,
) {
  useGridEvent(apiRef, eventName, handler, OPTIONS_IS_FIRST);
}

// TODO: move to @mui/x-data-grid/internals
// eslint-disable-next-line @typescript-eslint/naming-convention
export function unstable_resetCleanupTracking() {
  registryContainer.current?.reset();
  registryContainer.current = createRegistry();
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const internal_registryContainer = registryContainer;

function createRegistry() {
  return typeof FinalizationRegistry !== 'undefined'
    ? new FinalizationRegistryBasedCleanupTracking()
    : new TimerBasedCleanupTracking();
}
